import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  MapPin, Phone, User, Plus, Pencil, Trash2, Star, Loader2, Save, X, Building,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PM Spec V3 13/05/2026 — ClientContactsSettings
 *
 * Portail client : CRUD des contacts récurrents par site.
 * Utilisés pour pré-remplir les contacts de stops sur les opérations.
 */
const ClientContactsSettings = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // contact id en édition (ou 'new')
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: 'Bearer cookie-auth' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/client/contacts`, { headers });
      setContacts(r.data?.contacts || []);
    } catch (err) {
      toast({ title: 'Chargement impossible', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({
      siteAddress: '', siteCity: '', siteLabel: '',
      contactName: '', contactPhone: '', contactRole: '',
      isDefault: false,
    });
    setEditing('new');
  };

  const openEdit = (contact) => {
    setForm({ ...contact });
    setEditing(contact.id);
  };

  const cancel = () => { setEditing(null); setForm({}); };

  const save = async () => {
    if (!form.siteAddress || !form.contactName || !form.contactPhone) {
      toast({ title: 'Champs obligatoires manquants', description: 'Adresse, nom, téléphone', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/client/contacts`, form, { headers });
        toast({ title: 'Contact ajouté' });
      } else {
        await axios.patch(`${API}/client/contacts/${editing}`, form, { headers });
        toast({ title: 'Contact mis à jour' });
      }
      setEditing(null); setForm({});
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast({ title: 'Échec', description: detail, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (contactId) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    try {
      await axios.delete(`${API}/client/contacts/${contactId}`, { headers });
      toast({ title: 'Contact supprimé' });
      load();
    } catch (err) {
      toast({ title: 'Échec', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto p-4 sm:p-6" data-testid="client-contacts-settings">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-emerald-600" />
                Mes sites & contacts
              </CardTitle>
              <CardDescription>
                Enregistrez les contacts récurrents de vos sites de chargement et livraison.
                Ils seront pré-remplis automatiquement lors de la signature des bons.
              </CardDescription>
            </div>
            <Button data-testid="contact-add-btn" onClick={openNew}
                    className="bg-emerald-600 hover:bg-emerald-700 gap-1">
              <Plus className="w-4 h-4" /> Ajouter
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Form */}
      {editing && (
        <Card className="border-emerald-300 bg-emerald-50/40" data-testid="contact-form">
          <CardContent className="p-5 space-y-3">
            <div className="text-base font-semibold text-slate-900">
              {editing === 'new' ? 'Nouveau contact' : 'Modifier le contact'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Adresse du site *</Label>
                <Input
                  data-testid="contact-form-siteAddress"
                  value={form.siteAddress || ''}
                  onChange={(e) => setForm({ ...form, siteAddress: e.target.value })}
                  placeholder="Ex: Treichville Avenue 21"
                />
              </div>
              <div>
                <Label className="text-xs">Ville</Label>
                <Input
                  data-testid="contact-form-siteCity"
                  value={form.siteCity || ''}
                  onChange={(e) => setForm({ ...form, siteCity: e.target.value })}
                  placeholder="Ex: Abidjan"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Libellé court (optionnel)</Label>
                <Input
                  data-testid="contact-form-siteLabel"
                  value={form.siteLabel || ''}
                  onChange={(e) => setForm({ ...form, siteLabel: e.target.value })}
                  placeholder="Ex: Entrepôt principal"
                />
              </div>
              <div>
                <Label className="text-xs">Nom du contact *</Label>
                <Input
                  data-testid="contact-form-contactName"
                  value={form.contactName || ''}
                  onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                  placeholder="Ex: M. Diabaté"
                />
              </div>
              <div>
                <Label className="text-xs">Téléphone (WhatsApp) *</Label>
                <Input
                  data-testid="contact-form-contactPhone"
                  value={form.contactPhone || ''}
                  onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                  placeholder="+225 07 12 34 56"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Fonction</Label>
                <Input
                  data-testid="contact-form-contactRole"
                  value={form.contactRole || ''}
                  onChange={(e) => setForm({ ...form, contactRole: e.target.value })}
                  placeholder="Ex: Magasinier, Réceptionniste, Gérant…"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="contact-form-isDefault"
                checked={!!form.isDefault}
                onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                className="w-4 h-4 accent-emerald-600"
              />
              <span className="text-sm">Définir comme contact par défaut</span>
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={cancel} data-testid="contact-form-cancel" disabled={saving}>
                <X className="w-4 h-4 mr-1" /> Annuler
              </Button>
              <Button onClick={save} disabled={saving} data-testid="contact-form-save"
                      className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      ) : contacts.length === 0 ? (
        <Card className="bg-slate-50">
          <CardContent className="p-8 text-center text-slate-500">
            <MapPin className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <div className="text-sm">Aucun contact enregistré.</div>
            <div className="text-xs mt-1">Ajoutez vos sites pour gagner du temps.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="contacts-list">
          {contacts.map((c) => (
            <Card key={c.id} data-testid={`contact-card-${c.id}`} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {c.siteLabel && (
                      <div className="text-xs uppercase font-bold text-emerald-700 tracking-wide">
                        {c.siteLabel}
                      </div>
                    )}
                    <div className="font-semibold text-slate-900 truncate">{c.siteAddress}</div>
                    {c.siteCity && <div className="text-xs text-slate-500">{c.siteCity}</div>}
                  </div>
                  {c.isDefault && (
                    <Badge className="bg-amber-500 text-white border-0 gap-1 shrink-0">
                      <Star className="w-3 h-3" /> Défaut
                    </Badge>
                  )}
                </div>
                <div className="text-sm space-y-1 pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-slate-700">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-medium">{c.contactName}</span>
                    {c.contactRole && <span className="text-xs text-slate-500">— {c.contactRole}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                    <span>{c.contactPhone}</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm"
                          data-testid={`contact-edit-${c.id}`}
                          onClick={() => openEdit(c)} className="gap-1.5">
                    <Pencil className="w-3 h-3" /> Modifier
                  </Button>
                  <Button variant="outline" size="sm"
                          data-testid={`contact-delete-${c.id}`}
                          onClick={() => remove(c.id)}
                          className="gap-1.5 text-red-600 hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="w-3 h-3" /> Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientContactsSettings;
